import React, { useCallback, useState } from "react";
import { InventoryProduct } from "services/types/inventory-products.type";

type InventoryProductFieldProviderProps = {
  children?: React.ReactNode;
};

export type InventoryProductSelectedInfo = {
  selectedQuantity: number;
  isSelectedById: boolean;
};

const InventoryProductFieldContext = React.createContext<{
  getIsInventoryProductSelectedInfo: (
    inventoryProduct: InventoryProduct,
    name: string
  ) => InventoryProductSelectedInfo;
  rememberSelectedInventoryProduct: (
    inventoryProduct: InventoryProduct,
    name: string
  ) => void;
  forgetSelectedInventoryProduct: (name: string) => void;
} | null>(null);

type SelectedInventoryProductDetails = {
  field: string;
  id?: number;
};

type SelectedInventoryProductsState = {
  selectedByField: Record<string, number>;
  selectedInventoryProductDetails: Record<
    number,
    SelectedInventoryProductDetails[]
  >;
};

export function InventoryProductFieldProvider({
  children,
}: InventoryProductFieldProviderProps) {
  const [selectedInventoryProducts, setSelectedInventoryProducts] =
    useState<SelectedInventoryProductsState>({
      selectedByField: {},
      selectedInventoryProductDetails: {},
    });

  const getIsInventoryProductSelectedInfo = useCallback(
    (inventoryProduct: InventoryProduct, name: string) => {
      const { id } = inventoryProduct;

      const { selectedInventoryProductDetails } = selectedInventoryProducts;
      const selected = (selectedInventoryProductDetails[id] || []).filter(
        ({ field }) => field !== name
      );

      return {
        selectedQuantity: selected.length,
        isSelectedById:
          Boolean(id) &&
          selected.some(({ id: inventoryId }) => inventoryId === id),
      } as InventoryProductSelectedInfo;
    },
    [selectedInventoryProducts]
  );

  const rememberSelectedInventoryProduct = useCallback(
    (inventoryProduct: InventoryProduct, name: string) => {
      const { id } = inventoryProduct;
      const { selectedByField, selectedInventoryProductDetails } =
        structuredClone(
          selectedInventoryProducts
        ) as SelectedInventoryProductsState;

      if (selectedByField[name] && selectedByField[name] !== id) {
        selectedInventoryProductDetails[selectedByField[name]] =
          selectedInventoryProductDetails[selectedByField[name]].filter(
            ({ field }) => field !== name
          );
      }
      selectedByField[name] = id;

      if (!selectedInventoryProductDetails[id]) {
        selectedInventoryProductDetails[id] = [];
      }

      selectedInventoryProductDetails[id].push({
        field: name,
        id,
      });

      setSelectedInventoryProducts({
        selectedByField,
        selectedInventoryProductDetails,
      });
    },
    [selectedInventoryProducts]
  );

  const forgetSelectedInventoryProduct = useCallback(
    (name: string) => {
      const { selectedByField, selectedInventoryProductDetails } =
        structuredClone(
          selectedInventoryProducts
        ) as SelectedInventoryProductsState;
      const productId = selectedByField[name];
      if (!productId) return;
      selectedInventoryProductDetails[productId] =
        selectedInventoryProductDetails[productId].filter(
          ({ field }) => field !== name
        );
      delete selectedByField[name];

      setSelectedInventoryProducts({
        selectedByField,
        selectedInventoryProductDetails,
      });
    },
    [selectedInventoryProducts]
  );

  return (
    <InventoryProductFieldContext.Provider
      value={{
        getIsInventoryProductSelectedInfo,
        rememberSelectedInventoryProduct,
        forgetSelectedInventoryProduct,
      }}
    >
      {children}
    </InventoryProductFieldContext.Provider>
  );
}

export default function useInventoryProductField() {
  const context = React.useContext(InventoryProductFieldContext);

  if (!context) return {};

  const {
    getIsInventoryProductSelectedInfo,
    rememberSelectedInventoryProduct,
    forgetSelectedInventoryProduct,
  } = context;

  return {
    getIsInventoryProductSelectedInfo,
    rememberSelectedInventoryProduct,
    forgetSelectedInventoryProduct,
  };
}
